/**
 * SyncStatusProvider
 *
 * Provides global sync status context to the app.
 * Tracks online/offline state, pending mutations, and sync progress.
 */

import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { syncManager } from 'common/offline/syncManager';
import { useMutationQueue } from 'common/offline/useMutationQueue';
import { useOnlineStatus } from 'common/offline/useOnlineStatus';
import { SyncProgress } from 'common/offline/types';

export type SyncStatus = 'online' | 'offline' | 'syncing' | 'pending' | 'error';

export type SyncStatusContextValue = {
  /** Current sync status */
  status: SyncStatus;
  /** Whether the app is online */
  isOnline: boolean;
  /** Whether sync is in progress */
  isSyncing: boolean;
  /** Number of pending mutations */
  pendingCount: number;
  /** Number of failed mutations */
  failedCount: number;
  /** Current sync progress (null if not syncing) */
  syncProgress: SyncProgress | null;
  /** Whether the app was recently offline */
  wasOffline: boolean;
  /** Whether there are local changes (pending or failed mutations) */
  hasLocalChanges: boolean;
  /** Whether a discard operation is in progress */
  isDiscarding: boolean;
  /** Clear the wasOffline flag */
  clearWasOffline: () => void;
  /** Manually trigger sync */
  startSync: () => void;
  /** Retry failed mutations */
  retryFailed: () => void;
  /** Discard all local changes and reset to server state */
  discardLocalChanges: () => Promise<void>;
};

const SyncStatusContext = createContext<SyncStatusContextValue | null>(null);

export const useSyncStatus = (): SyncStatusContextValue => {
  const context = useContext(SyncStatusContext);

  if (!context) {
    throw new Error('useSyncStatus must be used within SyncStatusProvider');
  }

  return context;
};

export const SyncStatusProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isOnline, wasOffline, clearWasOffline } = useOnlineStatus();
  const { pendingCount, isSyncing, syncProgress, startSync, retryFailed, queue } = useMutationQueue();
  const [failedCount, setFailedCount] = useState(0);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Calculate failed count from queue
  useEffect(() => {
    const failed = queue.filter((m) => m.status === 'failed').length;
    setFailedCount(failed);
  }, [queue]);

  // Derive overall status
  const status = useMemo((): SyncStatus => {
    if (!isOnline) {
      return 'offline';
    }

    if (isSyncing) {
      return 'syncing';
    }

    if (failedCount > 0) {
      return 'error';
    }

    if (pendingCount > 0) {
      return 'pending';
    }

    return 'online';
  }, [isOnline, isSyncing, failedCount, pendingCount]);

  // Check if there are any local changes (pending or failed mutations)
  const hasLocalChanges = useMemo(() => {
    return pendingCount > 0 || failedCount > 0;
  }, [pendingCount, failedCount]);

  // Discard all local changes
  const discardLocalChanges = useCallback(async () => {
    setIsDiscarding(true);
    try {
      await syncManager.discardLocalChanges();
    } finally {
      setIsDiscarding(false);
    }
  }, []);

  const value = useMemo(
    (): SyncStatusContextValue => ({
      status,
      isOnline,
      isSyncing,
      pendingCount,
      failedCount,
      syncProgress,
      wasOffline,
      hasLocalChanges,
      isDiscarding,
      clearWasOffline,
      startSync,
      retryFailed,
      discardLocalChanges,
    }),
    [
      status,
      isOnline,
      isSyncing,
      pendingCount,
      failedCount,
      syncProgress,
      wasOffline,
      hasLocalChanges,
      isDiscarding,
      clearWasOffline,
      startSync,
      retryFailed,
      discardLocalChanges,
    ],
  );

  return <SyncStatusContext.Provider value={value}>{children}</SyncStatusContext.Provider>;
};
